import { supabase, ensureSession } from './client';

export interface HomeOwnerObjectType {
  id: string;
  name: string;
  type: string;
  location?: string | null;
  address?: string | null;
  description?: string | null;
  last_maintenance?: string | null;
  status?: string | null;
  owner_id: string | null;
  created_at: string;
  updated_at: string;
}

export type CreateHomeOwnerObjectData = Omit<HomeOwnerObjectType, 'id' | 'created_at' | 'updated_at' | 'owner_id'>;
export type UpdateHomeOwnerObjectData = Partial<Omit<HomeOwnerObjectType, 'id' | 'created_at' | 'updated_at' | 'owner_id'>>;

export const homeOwnerObjectsService = {
  getAll: async (): Promise<HomeOwnerObjectType[]> => {
    // Wait for the restored session before querying, otherwise this runs as
    // anon → RLS returns [] with no error → the empty list gets cached/persisted
    // and the property selector shows "No properties available" (#2907).
    const session = await ensureSession();
    if (!session) {
      // Signed out (or session never arrived): throw so react-query treats this
      // as an error and RETRIES — never caches a false-empty as a success.
      throw new Error('No active session — cannot load properties');
    }

    const { data, error } = await supabase
      .from('home_owner_objects')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /**
   * Properties the current user OWNS (owner_id = me), excluding ones they can
   * only see as an assigned member. getAll() returns both (RLS grants members
   * read access), which is wrong for flows that must act on OWNED properties
   * only — e.g. the "grant access to" list when inviting a member: an owner may
   * only share their own houses, never a house they are merely a member of.
   */
  getOwned: async (): Promise<HomeOwnerObjectType[]> => {
    const session = await ensureSession();
    if (!session) {
      throw new Error('No active session — cannot load properties');
    }
    const { data, error } = await supabase
      .from('home_owner_objects')
      .select('*')
      .eq('owner_id', session.user.id)
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  getById: async (id: string): Promise<HomeOwnerObjectType | null> => {
    const { data, error } = await supabase
      .from('home_owner_objects')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  create: async (input: CreateHomeOwnerObjectData): Promise<HomeOwnerObjectType> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('home_owner_objects')
      .insert({
        ...input,
        owner_id: user.id,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  createWithId: async (id: string, input: CreateHomeOwnerObjectData): Promise<HomeOwnerObjectType> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('home_owner_objects')
      .insert({
        id,
        ...input,
        owner_id: user.id,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  update: async (id: string, input: UpdateHomeOwnerObjectData): Promise<HomeOwnerObjectType> => {
    const { data, error } = await supabase
      .from('home_owner_objects')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('home_owner_objects')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};


